/** @fileoverview Reads and caches the values system fields can take (picklist values, foundation object records), and turns them into labels for display. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { stableStringify } from '../../lib/utils';
import type {
  FabrikAppDefinition,
  FabrikComponent,
  FabrikFieldOption,
  FabrikFieldOptions,
  FabrikFieldOptionsAnswer,
  FabrikFieldValuesPage,
  FabrikFieldValuesRequest,
  FabrikODataDataSource,
  FabrikSystemField,
} from '../../types/apps';

/** Which fields of which entity of which system to read options for. */
export type FieldOptionsRequest = { systemId: string; entity: string; fields: string[] };

/** Reads field options by field name; only fields that have options are in the answer. */
export type FieldOptionsReader = (request: FieldOptionsRequest) => Promise<FabrikFieldOptions>;

/** Reads a search page of one system field's values, or the labels of some codes. */
export type FieldValuesReader = (
  request: FabrikFieldValuesRequest
) => Promise<FabrikFieldValuesPage>;

/* Most codes one label lookup may ask for, and how long new codes wait to be asked together. */
const MAX_LABEL_CODES = 200;
const LABEL_BATCH_DELAY_MS = 50;
/* How many times a code whose label lookup failed is asked again on one page. */
const MAX_LABEL_RETRIES = 2;
/** Longest code a label lookup accepts. */
export const MAX_CODE_LENGTH = 256;

/**
 * Wraps a field options call so each field is read once per page: fields asked for together go
 * in one request, and a field asked for again shares the first answer. A failed read is
 * forgotten, so a later ask tries again.
 * @param read the call that reads field options for one mode (builder or hosted)
 */
export const createFieldOptionsReader = (read: FieldOptionsReader): FieldOptionsReader => {
  const cache = new Map<string, Promise<FabrikFieldOptionsAnswer | undefined>>();
  return async ({ systemId, entity, fields }) => {
    const keyOf = (field: string) => `${systemId}\u0000${entity}\u0000${field}`;
    const wanted = [...new Set(fields)];
    const missing = wanted.filter((field) => !cache.has(keyOf(field)));
    if (missing.length) {
      const batch = read({ systemId, entity, fields: missing });
      missing.forEach((field) =>
        cache.set(
          keyOf(field),
          batch
            .then((answer) => answer[field])
            .catch((reason: unknown) => {
              cache.delete(keyOf(field));
              throw reason;
            })
        )
      );
    }
    const entries = await Promise.all(
      wanted.map(async (field) => [field, await cache.get(keyOf(field))] as const)
    );
    return Object.fromEntries(
      entries.flatMap(([field, answer]) => (answer ? [[field, answer]] : []))
    );
  };
};

/**
 * Wraps a field values call. A label lookup (a request with `values`) is sent once per page and
 * later asks share its answer. A search page is not kept, since the values can change; only
 * identical searches sent at the same time share one call. A failed read is forgotten, so a
 * later ask tries again.
 * @param read the call that reads field values for one mode (builder or hosted)
 */
export const createFieldValuesReader = (read: FieldValuesReader): FieldValuesReader => {
  const cache = new Map<string, Promise<FabrikFieldValuesPage>>();
  return (request) => {
    const key = stableStringify(request);
    const cached = cache.get(key);
    if (cached) {
      return cached;
    }
    const keepsAnswer = Boolean(request.values);
    const answer = read(request)
      .catch((reason: unknown) => {
        cache.delete(key);
        throw reason;
      })
      .finally(() => {
        if (!keepsAnswer) {
          cache.delete(key);
        }
      });
    cache.set(key, answer);
    return answer;
  };
};

/**
 * Keeps the first option of each value. A source can repeat a value (one row per picklist
 * version, or a search page that repeats a value an earlier page gave).
 * @param options options in the order they arrived
 */
export const uniqueByValue = <Option extends { value: unknown }>(options: Option[]): Option[] => {
  const seen = new Set<unknown>();
  return options.filter(({ value }) => !seen.has(value) && Boolean(seen.add(value)));
};

/**
 * Maps every form a read can give a field value in (external code, or option id for legacy
 * picklists) to its label.
 * @param options the field's options
 */
export const labelsByValue = (options: FabrikFieldOption[]): Map<string, string> =>
  new Map(
    options.flatMap(({ value, label, optionId }) => [
      [value, label] as const,
      ...(optionId ? [[optionId, label] as const] : []),
    ])
  );

/** Gives the label of a value read from a data source field, when the field has labelled values. */
export type LabelOf = (dataSourceId: string, field: string, value: unknown) => string | undefined;

/**
 * Lists the OData data sources of every list in a definition, including nested Grid and Card items.
 * @param definition the app definition
 */
export const odataSourcesOf = (definition: FabrikAppDefinition): FabrikODataDataSource[] => {
  const collect = (components: FabrikComponent[]): FabrikODataDataSource[] =>
    components.flatMap((component) => {
      if (component.kind === 'grid')
        return collect(component.items.map(({ component: child }) => child));
      if (component.kind === 'card') return collect([component.content]);
      return component.kind === 'list'
        ? component.dataSource.flatMap((source) => (source.type === 'odata' ? [source] : []))
        : [];
    });
  return definition.screens.flatMap(({ components }) => collect(components));
};

/**
 * Lists, per data source id, the plain fields the definition shows: references that read a list
 * row or the record on screen. Fields read through a navigation are left out.
 * @param definition the app definition, walked as data
 */
const shownFieldsOf = (definition: FabrikAppDefinition): Map<string, Set<string>> => {
  const shown = new Map<string, Set<string>>();
  const visit = (node: unknown) => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (!node || typeof node !== 'object') return;
    const { type, scope, dataSourceId, field } = node as Record<string, unknown>;
    if (
      type === 'ref' &&
      (scope === 'dataSource' || scope === 'record') &&
      typeof dataSourceId === 'string' &&
      typeof field === 'string' &&
      !field.includes('/')
    ) {
      shown.set(dataSourceId, (shown.get(dataSourceId) ?? new Set()).add(field));
    }
    Object.values(node).forEach(visit);
  };
  visit(definition.screens);
  return shown;
};

/**
 * Names one system field, as the key of the codes asked for it and their labels.
 * @param systemField the system, entity and field
 */
const fieldKeyOf = ({ systemId, entity, field }: FabrikSystemField) =>
  `${systemId}\u0000${entity}\u0000${field}`;

/**
 * Reads, once per page, the labelled values of the fields each OData data source shows
 * (picklists and foundation objects), and gives a function that finds the label of a value.
 * Each source's labels apply as soon as they arrive; values without a label show as they are.
 * A searched field (a large foundation object) has no whole list: the codes the screen asks a
 * label for are collected and looked up together, each code once per page.
 * @param definition the app definition
 * @param readFieldOptions the cached field options reader
 * @param readFieldValues the cached field values reader
 */
export const useDataSourceLabels = (
  definition: FabrikAppDefinition,
  readFieldOptions: FieldOptionsReader,
  readFieldValues: FieldValuesReader
): LabelOf => {
  const sources = useMemo(() => {
    const shown = shownFieldsOf(definition);
    return odataSourcesOf(definition).flatMap(({ id, systemId, entity }) => {
      const fields = [...(shown.get(id) ?? [])];
      return fields.length ? [{ id, systemId, entity, fields }] : [];
    });
  }, [definition]);
  const sourcesKey = stableStringify(sources);
  const [labels, setLabels] = useState<Map<string, Map<string, Map<string, string>>>>(new Map());
  /* The searched fields of each data source, by data source id and field name. */
  const [searchedFields, setSearchedFields] = useState<Map<string, Map<string, FabrikSystemField>>>(
    new Map()
  );
  /* Labels found for the codes of searched fields, by field key and code. */
  const [codeLabels, setCodeLabels] = useState<Map<string, Map<string, string>>>(new Map());

  useEffect(() => {
    let isCurrent = true;
    sources.forEach(({ id, systemId, entity, fields }) => {
      readFieldOptions({ systemId, entity, fields })
        .then((answer) => {
          if (!isCurrent) return;
          const byField = new Map<string, Map<string, string>>();
          const searched = new Map<string, FabrikSystemField>();
          Object.entries(answer).forEach(([field, fieldAnswer]) => {
            if (fieldAnswer.kind === 'list') {
              byField.set(field, labelsByValue(fieldAnswer.options));
            } else if (fieldAnswer.kind === 'search') {
              searched.set(field, { systemId, entity, field });
            }
          });
          setLabels((previous) => new Map(previous).set(id, byField));
          setSearchedFields((previous) => new Map(previous).set(id, searched));
        })
        .catch(() => undefined);
    });
    return () => {
      isCurrent = false;
    };
    // sourcesKey stands for the sources; readFieldOptions is stable per page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesKey, readFieldOptions]);

  /* Codes asked or queued per field key, and how many lookups failed per field key and code. */
  const askedCodes = useRef(new Map<string, Set<string>>());
  const failedCodes = useRef(new Map<string, Map<string, number>>());
  /*
   * The queue lives outside any effect, so a new reader does not drop the codes queued so far.
   * The reader is read through a ref when the queue is sent.
   */
  const queuedCodes = useRef(
    new Map<string, { systemField: FabrikSystemField; codes: Set<string> }>()
  );
  const queueTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentReader = useRef(readFieldValues);
  const isMounted = useRef(false);

  /**
   * Forgets codes whose lookup failed, so a later ask tries them again, up to MAX_LABEL_RETRIES
   * times per code.
   * @param key the field key
   * @param codes the codes of the failed lookup
   */
  const forgetFailedCodes = useCallback((key: string, codes: string[]) => {
    const failedForField = failedCodes.current.get(key) ?? new Map<string, number>();
    failedCodes.current.set(key, failedForField);
    codes.forEach((code) => {
      const failures = (failedForField.get(code) ?? 0) + 1;
      failedForField.set(code, failures);
      if (failures <= MAX_LABEL_RETRIES) {
        askedCodes.current.get(key)?.delete(code);
      }
    });
  }, []);

  /** Sends the queued codes, at most MAX_LABEL_CODES per call, and keeps the labels found. */
  const sendQueuedCodes = useCallback(() => {
    queueTimer.current = null;
    const batches = [...queuedCodes.current.values()];
    queuedCodes.current.clear();
    const read = currentReader.current;
    batches.forEach(({ systemField, codes: codeSet }) => {
      const key = fieldKeyOf(systemField);
      const codes = [...codeSet];
      const chunkCount = Math.ceil(codes.length / MAX_LABEL_CODES);
      Array.from({ length: chunkCount }, (_unused, chunkIndex) =>
        codes.slice(chunkIndex * MAX_LABEL_CODES, (chunkIndex + 1) * MAX_LABEL_CODES)
      ).forEach((values) => {
        read({ ...systemField, values })
          .then(({ results }) => {
            if (!isMounted.current || !results.length) {
              return;
            }
            setCodeLabels((previous) => {
              const byCode = new Map(previous.get(key));
              results.forEach(({ value, label }) => byCode.set(value, label));
              return new Map(previous).set(key, byCode);
            });
          })
          .catch(() => forgetFailedCodes(key, values));
      });
    });
  }, [forgetFailedCodes]);

  /**
   * Queues a code for a label lookup, unless it was asked already.
   * @param systemField the field the code belongs to
   * @param code the code to find a label for
   */
  const askCode = useCallback(
    (systemField: FabrikSystemField, code: string) => {
      const key = fieldKeyOf(systemField);
      const askedForField = askedCodes.current.get(key) ?? new Set<string>();
      if (askedForField.has(code)) {
        return;
      }
      askedCodes.current.set(key, askedForField.add(code));
      const queuedForField = queuedCodes.current.get(key) ?? { systemField, codes: new Set() };
      queuedForField.codes.add(code);
      queuedCodes.current.set(key, queuedForField);
      if (!queueTimer.current && isMounted.current) {
        queueTimer.current = setTimeout(sendQueuedCodes, LABEL_BATCH_DELAY_MS);
      }
    },
    [sendQueuedCodes]
  );

  /* A new reader starts a new page: codes asked before are asked again through it. */
  useEffect(() => {
    if (currentReader.current !== readFieldValues) {
      currentReader.current = readFieldValues;
      askedCodes.current.clear();
      failedCodes.current.clear();
    }
  }, [readFieldValues]);

  /* Codes queued before the first mount are sent now; unmount stops the timer. */
  useEffect(() => {
    isMounted.current = true;
    if (queuedCodes.current.size && !queueTimer.current) {
      queueTimer.current = setTimeout(sendQueuedCodes, LABEL_BATCH_DELAY_MS);
    }
    return () => {
      isMounted.current = false;
      if (queueTimer.current) {
        clearTimeout(queueTimer.current);
        queueTimer.current = null;
      }
    };
  }, [sendQueuedCodes]);

  return useCallback<LabelOf>(
    (dataSourceId, field, value) => {
      if (typeof value !== 'string' && typeof value !== 'number') return undefined;
      const code = String(value);
      const listed = labels.get(dataSourceId)?.get(field);
      if (listed) return listed.get(code);
      const systemField = searchedFields.get(dataSourceId)?.get(field);
      if (!systemField) return undefined;
      const known = codeLabels.get(fieldKeyOf(systemField))?.get(code);
      if (known === undefined && code && code.length <= MAX_CODE_LENGTH) {
        askCode(systemField, code);
      }
      return known;
    },
    [askCode, codeLabels, labels, searchedFields]
  );
};
